import {safeRedirectPath} from './useSafeRedirect';

/**
 * What every account must have before it can be used, and the age rules.
 *
 * Case-for-case mirror of the API's AccountSetupRules and the app's
 * account_setup.dart — change all three together. Pure; no requests.
 *
 * The terms are accepted as the account is created, not shortly afterwards,
 * because that is the moment the contract forms. The date of birth is what
 * tells us who is a junior, so it is asked at the same moment, of everyone, by
 * both ways in: the registration form and the social sign-in completion step.
 *
 * THOSE TWO ARE ALL THAT IS LEFT IN CODE. Where somebody plays, which country
 * that is in and which competitions they enter are asked by the SIGN-UP PROMPT
 * SET instead (SIGNUP_PROMPT_PLAN.md, 22 Sep 2026), authored in the console and
 * written by the prompt bindings. They can be, because a prompt is served to an
 * account that already exists; the terms and the date of birth cannot, because
 * they are answered before there is an account to serve one to. One renderer
 * and one write path, so the next sign-up question costs a console edit rather
 * than a field component and a release on two platforms.
 *
 * House style: imported explicitly, never auto-imported.
 */

/**
 * Below this, UK law says a child cannot consent to an online service themselves.
 *
 * NOTHING REFUSES ON THIS (18 Sep 2026). Every member who joined before we asked
 * has no date of birth, so enforcing it would have cut people off the moment they
 * answered honestly, with no notice. We ask, we apply the under-18 protections,
 * and how under-13s work is its own piece of work. Mirrors the API's
 * AccountSetupRules.IsBelowMinimumAge - change all three together.
 */
export const MINIMUM_AGE = 13;

/** Under 18s keep their account but lose the things a junior flag gates. */
export const ADULT_AGE = 18;

const EARLIEST_PLAUSIBLE_BIRTH = new Date(1900, 0, 1);

/**
 * Why each answer is asked, in the words shown under the field.
 *
 * SHORT, and each sentence earning its place. These sit on a form someone is
 * trying to finish, where a paragraph reads as an obstacle and goes unread, so
 * a fact that does not change the answer they give does not belong here (how
 * amateur sport is usually organised is true and was cut for that reason). No
 * dashes either: they read as written by a system.
 *
 * What must survive any trim is the PLAYING CATEGORY clarification. Someone
 * who identifies one way and plays in the other category has to understand
 * that this decides where they are placed, or they answer the wrong question
 * honestly and end up in the wrong competitions.
 *
 * Mirror of the API's AccountSetupRules help constants and the app's
 * AccountSetupHelp. Change all three together.
 */
/**
 * The date of birth help (owner decision B, 24 Sep 2026). Club admins see an
 * age and an Under 18 label, never the date, so the help says exactly that.
 * Also the default for a console question bound to the date of birth.
 */
export const DATE_OF_BIRTH_HELP = "Under-18s get extra protection: shown only by first name and initial on public pages, and no marketing emails. Your club's admins see your age, never your date of birth, and it is never public.";

export const SETUP_FIELD_HELP = Object.freeze({
  dateOfBirth: DATE_OF_BIRTH_HELP,
  location: 'So we can show you competitions and leaderboards near you. Never an address.',
  playingCategory: 'Decides which competitions you enter and which leaderboards you appear on. Choose the category you play in.',
});

/**
 * Which competitions someone enters, asked as exactly that.
 *
 * It is NOT a question about identity. What the product needs is which side of a
 * men's or women's competition to place someone on and which leaderboard to sort
 * them into, so that is what is asked — and somebody whose category differs from
 * how they identify can answer truthfully without being asked to misdescribe
 * themselves. That framing is also what makes it lawful to REQUIRE: a required
 * answer cannot rest on consent, which has to be freely given.
 *
 * No "prefer not to say" (Grant, 16 Sep 2026) and no free text — an open box
 * invites answers that are special category data under UK GDPR Art 9. Note null
 * still occurs: an admin-created player has no account and is never asked.
 *
 * Mirror of the API's PlayingCategoryOptions and the app's kPlayingCategories.
 */
export const PLAYING_CATEGORIES = Object.freeze([
  {value: 'W', title: "Women's"},
  {value: 'M', title: "Men's"},
]);

/**
 * A chosen value as the column stores it; anything else is dropped. "F" is read
 * as "W" because the column was called Gender and held F/M long before it was a
 * form, and those rows are answers.
 */
export function normalisePlayingCategory(chosen) {
  const wanted = typeof chosen === 'string' ? chosen.trim().toUpperCase() : '';
  if (wanted === 'F') return 'W';
  return PLAYING_CATEGORIES.some(o => o.value === wanted) ? wanted : null;
}

/**
 * The one word both clients print under a field nobody answered.
 *
 * The app already says exactly this under its county and category fields; the
 * web said nothing at all, which is what made a dead button unexplainable.
 */
export const REQUIRED_ANSWER = 'Required';

/**
 * Where a member held on the setup screen may still go.
 *
 * The screen asks them to accept the Terms of Use and the Privacy Policy, and
 * the guard was bouncing every navigation back to it, including the new tab
 * those links open. Consent to a document you are prevented from reading is not
 * informed consent, so the legal pages are an exit and have to be. Contact goes
 * with them: someone who cannot finish the screen needs a way to say so.
 *
 * Deliberately NARROWER than USER_FORM_PROMPT_EXITS. The profile page is not
 * here: an account that has not finished being created has nothing to manage
 * there, and it would turn the wall into a detour.
 */
export const SETUP_SCREEN_EXITS = Object.freeze([
  '/legal', '/contact', '/meets/offer/', '/pay/',
  // Stopping an email must always work, whatever is owed (29 Sep 2026).
  '/email-preferences',
]);

/**
 * Matches a path SEGMENT, not a word beginning, so "/legalese" is not an exit.
 * A prefix ending in "/" is matched as written, because those are id-carrying
 * one-click pages rather than a single route.
 */
export function isSetupScreenExit(path) {
  if (typeof path !== 'string') return false;
  return SETUP_SCREEN_EXITS.some((exit) => exit.endsWith('/')
      ? path.startsWith(exit)
      : path === exit || path.startsWith(exit + '/'));
}

/**
 * Where the setup guard sends a signed-in member, or null to let them through.
 *
 * ONLY `setupRequired` holds here: the terms and the date of birth an account
 * created without them still owes. A missing NAME used to hold here too; it is
 * the sign-up prompt set's required question now, asked by the prompt wall
 * (PROFILE_DETAILS_PLAN.md §9 item 2), so there is one stream of required
 * details rather than two. Trusted as the server sent it, never re-derived.
 *
 * An account with nothing owed that lands on the screen goes on to its
 * redirectUrl (a social sign-in that created the account is sent here with
 * one), so it never meets a form with nothing to ask. Only a same-site path is
 * followed (safeRedirectPath).
 */
export function setupScreenDestination(to, user) {
  const onScreen = to?.path === '/details-required';
  if (setupRequired(user)) {
    if (onScreen || isSetupScreenExit(to?.path)) return null;
    return '/details-required?redirectUrl=' + to.fullPath;
  }
  if (!onScreen) return null;
  return safeRedirectPath(to?.query?.redirectUrl);
}

const asDate = (value) => (value instanceof Date ? value : value ? new Date(value) : null);

/** Whole years old on a given day. */
export function ageOn(dateOfBirth, on = new Date()) {
  const dob = asDate(dateOfBirth);
  if (!dob) return null;
  let age = on.getFullYear() - dob.getFullYear();
  const beforeBirthday = on.getMonth() < dob.getMonth()
    || (on.getMonth() === dob.getMonth() && on.getDate() < dob.getDate());
  if (beforeBirthday) age--;
  return age;
}

/** Null when the date is usable, otherwise what to tell them. */
export function dateOfBirthProblem(dateOfBirth, now = new Date()) {
  const dob = asDate(dateOfBirth);
  if (!dob || isNaN(dob.getTime())) return 'Please enter your date of birth.';
  const day = new Date(dob.getFullYear(), dob.getMonth(), dob.getDate());
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (day > today) return 'That date is in the future.';
  if (day < EARLIEST_PLAUSIBLE_BIRTH) return 'Please check that date.';
  return null;
}

/**
 * The days a date-of-birth picker may offer, as `yyyy-MM-dd` strings.
 *
 * Deliberately the SAME two bounds `dateOfBirthProblem` rejects on, read from
 * the same constants: a calendar that lets someone click a day the validator
 * then refuses is worse than one that never offered it. Callers pass these
 * straight to TapDatePicker's `min` and `max`, whose own defaults are for
 * SCHEDULING (no earlier than today) and make a birthday unanswerable.
 */
export function dateOfBirthWindow(now = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return {
    min: iso(EARLIEST_PLAUSIBLE_BIRTH),
    max: iso(new Date(now.getFullYear(), now.getMonth(), now.getDate())),
  };
}

/** Too young for an account of their own; their club adds them as a junior player. */
export function isBelowMinimumAge(dateOfBirth, now = new Date()) {
  const age = ageOn(dateOfBirth, now);
  return age !== null && age < MINIMUM_AGE;
}

export function isMinor(dateOfBirth, now = new Date()) {
  const age = ageOn(dateOfBirth, now);
  return age !== null && age < ADULT_AGE;
}

/**
 * Hold this account on the setup screen.
 *
 * The API decides, and deliberately does NOT decide it from missing data: every
 * member who joined before these questions existed has no date of birth, and
 * holding them all on a registration screen they never asked for is wrong. They
 * are asked by a user form prompt instead. This is true only for an account
 * CREATED without the answers, which is a social sign-in or an old email link.
 */
export function setupRequired(user) {
  return !!user && user.setupRequired === true;
}
